import {
  SandboxShortcut,
  SandboxShortcutGroup,
  SandboxShortcutTemplate,
  SandboxShortcutType,
} from '../types';
import {
  ProductionGameShortcutFixture,
  productionGameShortcuts,
} from './productionGameShortcuts';
import { productionShortcutTemplateArtwork } from './productionShortcutTemplateArtwork';
import { productionShortcutTemplateDescriptions } from './productionShortcutTemplateDescriptions';

const encodeSvg = (svg: string): string =>
  `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;

const artwork = (
  title: string,
  from: string,
  to: string,
  aspect: 'cover' | 'wide' | 'icon' = 'cover'
): string => {
  const dimensions =
    aspect === 'wide'
      ? { width: 1280, height: 720 }
      : aspect === 'icon'
      ? { width: 512, height: 512 }
      : { width: 720, height: 960 };
  const fontSize = aspect === 'cover' ? 62 : aspect === 'wide' ? 70 : 48;

  return encodeSvg(`
    <svg xmlns="http://www.w3.org/2000/svg" width="${
      dimensions.width
    }" height="${dimensions.height}" viewBox="0 0 ${dimensions.width} ${
    dimensions.height
  }">
      <defs>
        <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stop-color="${from}"/>
          <stop offset="1" stop-color="${to}"/>
        </linearGradient>
        <radialGradient id="r" cx="75%" cy="20%" r="70%">
          <stop offset="0" stop-color="#ffffff" stop-opacity=".28"/>
          <stop offset="1" stop-color="#ffffff" stop-opacity="0"/>
        </radialGradient>
      </defs>
      <rect width="100%" height="100%" rx="28" fill="url(#g)"/>
      <rect width="100%" height="100%" rx="28" fill="url(#r)"/>
      <path d="M-40 ${dimensions.height * 0.72} L${dimensions.width * 0.72} ${
    dimensions.height * 0.18
  } L${dimensions.width + 80} ${dimensions.height * 0.55} L${
    dimensions.width * 0.25
  } ${dimensions.height + 60}Z" fill="#050713" opacity=".3"/>
      <text x="50%" y="52%" text-anchor="middle" dominant-baseline="middle" fill="#fff" font-family="Arial, sans-serif" font-size="${fontSize}" font-weight="700">${title}</text>
    </svg>
  `);
};

export const createShortcutGroups = (): SandboxShortcutGroup[] => [
  {
    origin: 'preset',
    id: 1,
    sort: 100,
    title: 'Без группы',
    groupType: 'DEFAULT',
  },
  {
    origin: 'preset',
    id: 2,
    sort: 90,
    title: 'Игры с клубными аккаунтами',
    groupType: 'GAME',
  },
  {
    origin: 'preset',
    id: 3,
    sort: 80,
    title: 'Оффлайн игры',
    groupType: 'GAME',
  },
  {
    origin: 'preset',
    id: 4,
    sort: 70,
    title: 'Лаунчеры',
    groupType: 'GAME',
  },
  {
    origin: 'preset',
    id: 5,
    sort: 60,
    title: 'Пополнить Steam',
    groupType: 'GAME',
  },
];

interface ShortcutFixture {
  title: string;
  type: SandboxShortcutType;
  group_id: number;
  color: [string, string];
  age?: number;
}

const shortcutFixtures: ShortcutFixture[] = [
  {
    title: 'Valorant',
    type: 'GAME',
    group_id: 1,
    color: ['#f04459', '#6d172c'],
    age: 16,
  },
  {
    title: 'League of Legends',
    type: 'GAME',
    group_id: 1,
    color: ['#14779f', '#061c3d'],
    age: 12,
  },
  {
    title: 'Overwatch 2',
    type: 'GAME',
    group_id: 1,
    color: ['#f28c28', '#30364f'],
    age: 12,
  },
  {
    title: 'GTA V',
    type: 'GAME',
    group_id: 1,
    color: ['#e55a65', '#325b8f'],
    age: 18,
  },
  {
    title: 'Escape from Tarkov',
    type: 'GAME',
    group_id: 1,
    color: ['#9a896d', '#222b2d'],
    age: 18,
  },
  {
    title: 'Counter-Strike 2',
    type: 'GAME',
    group_id: 2,
    color: ['#f3a83b', '#252b31'],
    age: 18,
  },
  {
    title: 'PUBG: Battlegrounds',
    type: 'GAME',
    group_id: 2,
    color: ['#d59a19', '#32351f'],
    age: 16,
  },
  {
    title: 'Dota 2',
    type: 'GAME',
    group_id: 2,
    color: ['#bb3934', '#251d22'],
    age: 12,
  },
  {
    title: 'Apex Legends',
    type: 'GAME',
    group_id: 2,
    color: ['#db493f', '#531a26'],
    age: 16,
  },
  {
    title: 'Fortnite',
    type: 'GAME',
    group_id: 2,
    color: ['#6f77e8', '#2f397e'],
    age: 12,
  },
  {
    title: 'Minecraft',
    type: 'GAME',
    group_id: 2,
    color: ['#62a549', '#31552f'],
    age: 6,
  },
  {
    title: 'Rocket League',
    type: 'GAME',
    group_id: 2,
    color: ['#2b83d7', '#163a78'],
    age: 6,
  },
  {
    title: 'S.T.A.L.K.E.R.',
    type: 'GAME',
    group_id: 3,
    color: ['#8b754b', '#252b23'],
    age: 18,
  },
  {
    title: 'Terraria',
    type: 'GAME',
    group_id: 3,
    color: ['#73ac48', '#31533d'],
    age: 12,
  },
  {
    title: 'Civilization VI',
    type: 'GAME',
    group_id: 3,
    color: ['#346ca9', '#15264b'],
    age: 12,
  },
  {
    title: 'The Elder Scrolls V: Skyrim',
    type: 'GAME',
    group_id: 3,
    color: ['#8694a6', '#1f2935'],
    age: 18,
  },
  { title: 'Steam', type: 'GAME', group_id: 4, color: ['#2785b8', '#142443'] },
  {
    title: 'Epic Games Launcher',
    type: 'GAME',
    group_id: 4,
    color: ['#555', '#111'],
  },
  {
    title: 'Battle.net',
    type: 'GAME',
    group_id: 4,
    color: ['#149ee5', '#17345e'],
  },
  {
    title: 'Ubisoft Connect',
    type: 'GAME',
    group_id: 4,
    color: ['#8666e9', '#25204d'],
  },
  {
    title: 'Пополнить Steam',
    type: 'GAME',
    group_id: 5,
    color: ['#3f92c7', '#172e4c'],
  },
  {
    title: 'Discord',
    type: 'APPLICATION',
    group_id: 1,
    color: ['#5865f2', '#343a8c'],
  },
  {
    title: 'Google Chrome',
    type: 'APPLICATION',
    group_id: 1,
    color: ['#e34d43', '#2e8b57'],
  },
  {
    title: 'Telegram',
    type: 'APPLICATION',
    group_id: 1,
    color: ['#32a9dc', '#17638c'],
  },
  {
    title: 'TeamSpeak 3',
    type: 'APPLICATION',
    group_id: 1,
    color: ['#3e82b8', '#24364f'],
  },
  {
    title: 'Radmin VPN',
    type: 'APPLICATION',
    group_id: 1,
    color: ['#f3b62f', '#d56b27'],
  },
  {
    title: 'FACEIT',
    type: 'APPLICATION',
    group_id: 1,
    color: ['#ff5e25', '#632315'],
  },
  {
    title: 'Logitech G HUB',
    type: 'APPLICATION',
    group_id: 1,
    color: ['#19afc7', '#16436b'],
  },
  {
    title: 'Razer Synapse',
    type: 'APPLICATION',
    group_id: 1,
    color: ['#45c849', '#16391d'],
  },
];

const isGrandTheftAuto = (title: string): boolean =>
  /^Grand Thef(?:t)? Auto V/.test(title);

const getDemoShortcutAgeRating = (
  fixture: ProductionGameShortcutFixture
): number | null => {
  if (fixture.title === 'Sirus') return 12;
  if (fixture.title === 'STALKER Call of Prypiat Enhanced Edition') return 16;
  if (isGrandTheftAuto(fixture.title)) return 18;

  return fixture.age_rating;
};

const requiresGuestRestriction = (
  fixture: ProductionGameShortcutFixture
): boolean =>
  fixture.title === 'Sirus' ||
  fixture.title === 'STALKER Call of Prypiat Enhanced Edition' ||
  isGrandTheftAuto(fixture.title);

export const createShortcuts = (): SandboxShortcut[] => {
  const groupIds: Record<string, number> = {
    'Игры без группы': 1,
    'Игры с клубными аккаунтами': 2,
    'Оффлайн игры': 3,
    Лаунчеры: 4,
    'Пополнить Steam': 5,
  };
  const games: SandboxShortcut[] = productionGameShortcuts.map(
    (fixture, index) => {
      const groupId = groupIds[fixture.groupTitle];
      if (!groupId) {
        throw new Error(`Неизвестная production-группа: ${fixture.groupTitle}`);
      }

      return {
        origin: 'preset',
        id: index + 1,
        type: 'GAME',
        title: fixture.title,
        group_id: groupId,
        sort: fixture.sort,
        popular: true,
        path: [`C:\\Games\\SmartShell\\game-${index + 1}.exe`],
        work_dir: null,
        args: '',
        main_picture: fixture.main_picture,
        screenshots: [],
        description: `${fixture.title} — игровой ярлык из демонстрационного каталога клуба.`,
        icon_path: '',
        run_as_admin: false,
        only_for_registered:
          fixture.only_for_registered || requiresGuestRestriction(fixture),
        age_rating: getDemoShortcutAgeRating(fixture),
        free_run: false,
        game_account_group:
          groupId === 2 ? { id: 1, title: 'Клубные аккаунты' } : null,
      };
    }
  );
  const applicationFixtures = shortcutFixtures.filter(
    (fixture) => fixture.type === 'APPLICATION'
  );
  const applications: SandboxShortcut[] = applicationFixtures.map(
    (fixture, index) => {
      const picture = artwork(
        fixture.title,
        fixture.color[0],
        fixture.color[1],
        'icon'
      );

      return {
        origin: 'preset',
        id: games.length + index + 1,
        type: 'APPLICATION',
        title: fixture.title,
        group_id: fixture.group_id,
        sort: index,
        popular: true,
        path: [`C:\\Program Files\\${fixture.title}\\${fixture.title}.exe`],
        work_dir: null,
        args: '',
        main_picture: picture,
        screenshots: [],
        description: '',
        icon_path: picture,
        run_as_admin: false,
        only_for_registered: false,
        age_rating: null,
        free_run: false,
        game_account_group: null,
      };
    }
  );

  return [...games, ...applications];
};

const productionTemplateTitles = [
  '4game',
  '7 Days to Die',
  'Валорант',
  'Казаки 3',
  'Калибр',
  'Мир кораблей',
  'Мир танков',
  'Террария',
  'Фортнайт',
  'Albion Online',
  'Amazing RP',
  'Among Us',
  'Apex Legends',
  'ARC Raiders',
  'Arena Breakout',
  'Arena Breakout: Infinite',
  'Arizona RP',
  'ARMA 3',
  'Assetto Corsa',
  'Atomic Heart',
  'Baldur’s Gate 3',
  'Battle.net',
  'Battlefield 1',
  'Battlefield 2042',
  'Battlefield 4',
  'Battlefield 6',
  'Battlefield V',
  'BeamNG.drive',
  'Black Desert',
  'BlueStacks',
  'Blur',
  'Bodycam',
  'Brawlhalla',
  'Call of Duty',
  'Call of Duty 4: Modern Warfare',
  'Call of Duty: Warzone',
  'City Car Driving',
  'Command & Conquer: Generals',
  'Command & Conquer: Generals – Zero Hour',
  'Command & Conquer: Red Alert 2',
  'Content Warning',
  'Counter-Strike',
  'Counter-Strike 1.6',
  'Counter-Strike 2',
  'Counter-Strike: Source',
  'Crossout',
  'Cyberpunk 2077',
  'Days Gone',
  'DayZ',
  'DayZ Launcher',
  'Dead by Daylight',
  'Deadlock',
  'Deceit',
  'Deep Rock Galactic',
  'Delta Force',
  'Destiny 2',
  'Discord',
  'DOOM Eternal',
  'Dota 2',
  'Drive Beyond Horizons',
  'Dying Light',
  'Dying Light 2',
  'Elden Ring',
  'Enlisted',
  'Epic Games Launcher',
  'Escape from Tarkov',
  'Euro Truck Simulator 2',
  'FACEIT',
  'Fall Guys',
  'Fallout 4',
  'Far Cry 3',
  'Far Cry 5',
  'Far Cry 6',
  'FlatOut 2',
  'Forza Horizon 4',
  'Forza Horizon 5',
  'FragPunk',
  'Garry’s Mod',
  'Genshin Impact',
  'Geometry Dash',
  'Ghost of Tsushima Director’s Cut',
  'God of War',
  'Grand Theft Auto V',
  'GTA 5 RP',
  'Half-Life',
  'Hearthstone',
  'HELLDIVERS 2',
  'Heroes of the Storm',
  'Hitman 3',
  'Hogwarts Legacy',
  'Honkai: Star Rail',
  'HoYoPlay',
  'Hunt: Showdown',
  'ICCup',
  'inZOI',
  'It Takes Two',
  'League of Legends',
  'Left 4 Dead',
  'Left 4 Dead 2',
  'Lethal Company',
  'Logitech G HUB',
  'Lost Light',
  'Mafia II',
  'Majestic RP',
  'Marvel Rivals',
  'Metro 2033 Redux',
  'Metro Exodus',
  'Minecraft',
  'Need for Speed Heat',
  'Need for Speed Payback',
  'Need for Speed: Most Wanted',
  'Once Human',
  'osu!',
  'Overwatch 2',
  'Paladins',
  'Path of Exile',
  'Path of Exile 2',
  'Payday 2',
  'PEAK',
  'Phasmophobia',
  'Portal 2',
  'PUBG Mobile',
  'PUBG: Battlegrounds',
  'R.E.P.O.',
  'Radmir RP',
  'Raft',
  'RAGE Multiplayer',
  'Ready or Not',
  'Red Dead Redemption 2',
  'Riot Client',
  'Roblox',
  'Rocket League',
  'Rockstar Games Launcher',
  'Rust',
  'S.T.A.L.K.E.R.',
  'SCP: Secret Laboratory',
  'Sea of Thieves',
  'Sniper: Ghost Warrior 2',
  'Sons of the Forest',
  'Squad',
  'STALCRAFT: X',
  'StarCraft II',
  'Stray',
  'Stumble Guys',
  'Tanks Blitz',
  'Team Fortress 2',
  'TeamSpeak 3',
  'The Finals',
  'The Forest',
  'The Outlast Trials',
  'The Sims 4',
  'Tom Clancy’s Rainbow Six Siege',
  'Ubisoft Connect',
  'Valheim',
  'VK Play Игровой центр',
  'War Thunder',
  'Warcraft III',
  'Warface',
  'Warframe',
  'Wargaming.net Game Center',
  'World of Tanks',
  'World of Warcraft',
  'World War Z',
  'Wuthering Waves',
  'Zenless Zone Zero',
] as const;

export const createShortcutTemplates = (): SandboxShortcutTemplate[] =>
  productionTemplateTitles.map((title, index) => {
    const templateArtwork = productionShortcutTemplateArtwork[title];
    if (!templateArtwork) {
      throw new Error(`Не найдены production-изображения шаблона: ${title}`);
    }
    const description = productionShortcutTemplateDescriptions[title];
    if (!description) {
      throw new Error(`Не найдено production-описание шаблона: ${title}`);
    }
    const args =
      title === 'Counter-Strike 2' || title === 'Dota 2' ? '-novid' : '';

    return {
      id: index + 1,
      title,
      args,
      main_picture: templateArtwork.main_picture,
      run_as_admin: false,
      age_rating: 0,
      description,
      screenshots: [...templateArtwork.screenshots],
    };
  });
